import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { BRAND } from "@/lib/brand";
import { scrollToId } from "@/hooks/useLenis";
import { useLang } from "@/lib/i18n";

const LINK_IDS = ["services", "why", "quote"];

export default function Navbar() {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    let last = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 24);
      // Hide when scrolling down (past the hero-ish threshold), show when scrolling up.
      if (y > last && y > 140) setHidden(true);
      else if (y < last) setHidden(false);
      last = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: "-110%", opacity: 0 }}
      animate={{ y: hidden ? "-110%" : "0%", opacity: hidden ? 0 : 1 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-black/10"
          : "bg-transparent border-b border-transparent"
      }`}
      data-testid="main-navbar"
    >
      <nav className="max-w-7xl mx-auto px-5 md:px-8 flex flex-col items-center py-4 md:py-5">
        {/* Centered brand lockup */}
        <button
          onClick={() => scrollToId("hero")}
          className="flex flex-col items-center group"
          data-testid="nav-logo-button"
        >
          <img
            src={BRAND.logo}
            alt={BRAND.full}
            className={`object-contain rounded-full ring-2 ring-[#1478FF]/30 shadow-[0_0_40px_-6px_rgba(20,120,255,0.7)] transition-all duration-500 group-hover:rotate-[6deg] group-hover:scale-105 ${
              scrolled
                ? "h-16 w-16 md:h-20 md:w-20"
                : "h-28 w-28 md:h-44 md:w-44"
            }`}
          />
          <span
            className={`font-display font-bold tracking-tight text-white leading-none text-center transition-all duration-500 ${
              scrolled ? "mt-2 text-2xl md:text-3xl" : "mt-4 text-4xl md:text-6xl"
            }`}
          >
            OCD
            <span className="block text-[11px] md:text-sm tracking-[0.36em] text-zinc-400 font-body uppercase mt-1.5">
              Entretien Cleaning
            </span>
          </span>
        </button>

        {/* Centered nav row */}
        <div className="mt-5 md:mt-6 flex items-center gap-5 md:gap-8">
          {LINK_IDS.map((id) => (
            <button
              key={id}
              onClick={() => scrollToId(id)}
              className="relative text-sm md:text-base font-semibold text-neutral-700 hover:text-neutral-900 transition-colors after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-[#1478FF] after:transition-all hover:after:w-full"
              data-testid={`nav-link-${id}`}
            >
              {t.nav[id]}
            </button>
          ))}
          <button
            onClick={() => scrollToId("quote")}
            className="bg-[#1478FF] hover:bg-[#3B8BFF] active:scale-95 text-white font-bold text-sm md:text-base px-5 md:px-6 py-2.5 md:py-3 rounded-full transition-all duration-300 shadow-[0_0_0_0_rgba(20,120,255,0.5)] hover:shadow-[0_0_28px_-2px_rgba(20,120,255,0.7)]"
            data-testid="nav-quote-button"
          >
            {t.nav.cta}
          </button>

          {/* Language toggle */}
          <div
            className="flex items-center gap-1 border border-black/15 rounded-full p-1"
            data-testid="lang-toggle"
          >
            {["fr", "en"].map((code) => (
              <button
                key={code}
                onClick={() => setLang(code)}
                className={`text-xs font-bold uppercase px-2.5 py-1 rounded-full transition-colors duration-300 ${
                  lang === code
                    ? "bg-[#1478FF] text-white"
                    : "text-neutral-500 hover:text-neutral-900"
                }`}
                data-testid={`lang-${code}`}
              >
                {code}
              </button>
            ))}
          </div>
        </div>
      </nav>
    </motion.header>
  );
}
